import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Alert,
  Divider,
} from '@mui/material';
import {
  AddLocationAlt,
  Done,
  DeleteSweep,
  Undo,
  Redo,
  ChevronLeft,
} from '@mui/icons-material';
import { RootState } from '../../store/store';
import {
  startPlacement,
  cancelPlacement,
  clearDraft,
  setPanelCollapsed,
  undo,
  redo,
} from '../../store/slices/missionPlannerSlice';
import { MissionSummary } from './MissionSummary';
import { HomeBaseControls } from './HomeBaseControls';
import { DroneGeofenceControls } from './DroneGeofenceControls';
import { WaypointList } from './WaypointList';
import { ProfilePanel } from './ProfilePanel';
import { ShapePalette } from './ShapePalette';
import { ShapeList } from './ShapeList';
import { SurveyPathControls } from './SurveyPathControls';
import { MissionIOControls } from './MissionIOControls';
import { MissionPersistencePanel } from './MissionPersistencePanel';
import { tutorialTarget } from '../../tutorial/tours';

/**
 * Planner sidebar: placement controls, the ordered waypoint list, and the
 * empty / error states for the draft.
 */
export const PlannerPanel: React.FC = () => {
  const dispatch = useDispatch();
  const waypoints = useSelector(
    (state: RootState) => state.missionPlanner.draft.waypoints
  );
  const placementMode = useSelector(
    (state: RootState) => state.missionPlanner.placementMode
  );
  const error = useSelector((state: RootState) => state.missionPlanner.error);
  const canUndo = useSelector(
    (state: RootState) => state.missionPlanner.past.length > 0
  );
  const canRedo = useSelector(
    (state: RootState) => state.missionPlanner.future.length > 0
  );

  const isPlacing = placementMode === 'placing';

  return (
    <Box
      component="section"
      aria-label="Mission planner"
      sx={{
        width: { xs: '100%', md: 360 },
        height: '100%',
        overflow: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        borderRight: 1,
        borderColor: 'divider',
        // On touch/coarse pointers, guarantee a ≥44px hit area for every
        // control (issue #325) without inflating the compact desktop layout.
        '@media (pointer: coarse)': {
          '& .MuiButtonBase-root': { minHeight: 44 },
          '& .MuiIconButton-root': { minWidth: 44, minHeight: 44 },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" component="h2">
          Mission Planner
        </Typography>
        <IconButton
          aria-label="Collapse panel"
          title="Collapse panel"
          onClick={() => dispatch(setPanelCollapsed(true))}
          sx={{ minWidth: 44, minHeight: 44 }}
        >
          <ChevronLeft />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" role="alert">
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        {isPlacing ? (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Done />}
            onClick={() => dispatch(cancelPlacement())}
            fullWidth
          >
            Done placing
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<AddLocationAlt />}
            onClick={() => dispatch(startPlacement())}
            fullWidth
            {...tutorialTarget('planner-add-waypoints')}
          >
            Add waypoints
          </Button>
        )}
        <IconButton
          aria-label="Undo"
          title="Undo (Ctrl+Z)"
          onClick={() => dispatch(undo())}
          disabled={!canUndo}
          sx={{ minWidth: 44, minHeight: 44 }}
        >
          <Undo />
        </IconButton>
        <IconButton
          aria-label="Redo"
          title="Redo (Ctrl+Shift+Z)"
          onClick={() => dispatch(redo())}
          disabled={!canRedo}
          sx={{ minWidth: 44, minHeight: 44 }}
        >
          <Redo />
        </IconButton>
        <IconButton
          aria-label="Clear all waypoints"
          onClick={() => dispatch(clearDraft())}
          disabled={waypoints.length === 0}
          sx={{ minWidth: 44, minHeight: 44 }}
        >
          <DeleteSweep />
        </IconButton>
      </Box>

      <Divider />

      {/* Which drone this is for, and how the route stands against its
          geofence. Above the route so the boundary is on the map before the
          first waypoint is placed (#451). */}
      <Box sx={{ pt: 2 }}>
        <DroneGeofenceControls />
      </Box>

      <Divider />

      {waypoints.length === 0 ? (
        // Empty state — helpful guidance, not a blank space.
        <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">
            No waypoints yet. Click <strong>Add waypoints</strong>, then click
            the map to build your route.
          </Typography>
        </Box>
      ) : (
        <>
          <MissionSummary />
          <Divider />
          <HomeBaseControls />
          <WaypointList />
          <Divider />
          <ProfilePanel />
        </>
      )}

      {/* Coverage-area tools are available regardless of waypoints. */}
      <Divider />
      <ShapePalette />
      <ShapeList />
      <SurveyPathControls />

      {/* Import is available in the empty state; export needs a route. */}
      <Divider />
      <MissionIOControls />

      {/* Backend save/load of drafts (owner-scoped). */}
      <Divider />
      <MissionPersistencePanel />
    </Box>
  );
};
